import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiEdit2,
  FiEdit3,
  FiEye,
  FiLayers,
  FiPlus,
  FiSearch,
  FiSend,
  FiTrash2,
} from 'react-icons/fi';
import '../styles/AllProposals.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { patchAdminSearch } from '../../../utils/adminDashboardNav';
import { formatAUD } from '../../../utils/formatCurrency';
import {
  STATUS_TABS,
  getStatusMeta,
  isExpired,
  isExpiringSoon,
  mapProposalRow,
  needsFollowUp,
} from '../utils/proposalUtils';
import CreateProposalModal from './CreateProposalModal';
import ProposalViewModal from './ProposalViewModal';

const FETCH_ALL_PROPOSALS = `
  query FindAllProposals {
    findAllProposals {
      id
      proposalNumber
      title
      clientName
      clientEmail
      value
      status
      version
      sentAt
      viewedAt
      signedAt
      signedName
      rejectReason
      expiresAt
      createdAt
    }
  }
`;

const REMOVE_PROPOSAL = `
  mutation RemoveProposal($id: String!) {
    removeProposal(id: $id)
  }
`;

const FORECAST_QUOTE_KEY = 'sas.forecastQuote';

function proposalTabFromSearch(search) {
  const raw = new URLSearchParams(String(search || '').replace(/^\?/, '')).get(
    'tab',
  );
  return STATUS_TABS.some((tab) => tab.key === raw) ? raw : 'ALL';
}

export default function AllProposals() {
  const location = useLocation();
  const navigate = useNavigate();
  const forecastHandled = useRef(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(() =>
    proposalTabFromSearch(location.search),
  );
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [viewId, setViewId] = useState(
    () =>
      new URLSearchParams(location.search || '').get('proposal') || null,
  );
  const [showCreate, setShowCreate] = useState(
    () =>
      new URLSearchParams(location.search || '').get('action') === 'add',
  );
  const [createPrefill, setCreatePrefill] = useState(null);
  const [editId, setEditId] = useState(
    () => new URLSearchParams(location.search || '').get('edit') || null,
  );
  const [deletingId, setDeletingId] = useState(null);

  const writeProposalSearch = (patch = {}, remove = []) => {
    const search = patchAdminSearch(
      location.search,
      { page: 'proposals', ...patch },
      remove,
    );
    if (search === (location.search || '')) return;
    navigate({ pathname: location.pathname || '/', search }, { replace: true });
  };

  const openProposal = (id) => {
    setEditId(null);
    setShowCreate(false);
    setViewId(id);
    writeProposalSearch({ proposal: id }, ['edit', 'action', 'ptab']);
  };

  const closeProposal = () => {
    setViewId(null);
    writeProposalSearch({}, ['proposal', 'ptab']);
  };

  const openEdit = (id) => {
    setViewId(null);
    setShowCreate(false);
    setEditId(id);
    writeProposalSearch({ edit: id }, ['proposal', 'action', 'ptab']);
  };

  const closeEdit = () => {
    setEditId(null);
    writeProposalSearch({}, ['edit']);
  };

  const openCreate = (prefill = null) => {
    setViewId(null);
    setEditId(null);
    setCreatePrefill(prefill);
    setShowCreate(true);
    writeProposalSearch({ action: 'add' }, ['proposal', 'edit', 'ptab']);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setCreatePrefill(null);
    writeProposalSearch({}, ['action']);
  };

  const selectStatusTab = (key) => {
    setActiveTab(key);
    writeProposalSearch({ tab: key === 'ALL' ? '' : key });
  };

  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await graphqlRequest(FETCH_ALL_PROPOSALS);
      const list = (res.findAllProposals || []).map(mapProposalRow);
      setRows(list);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  useEffect(() => {
    const next = new URLSearchParams(location.search || '');
    if (next.get('from') === 'forecast' && !forecastHandled.current) {
      forecastHandled.current = true;
      let prefill = null;
      try {
        const raw = sessionStorage.getItem(FORECAST_QUOTE_KEY);
        if (raw) prefill = JSON.parse(raw);
      } catch {
        prefill = null;
      }
      try {
        sessionStorage.removeItem(FORECAST_QUOTE_KEY);
      } catch {
        /* ignore */
      }
      setCreatePrefill(prefill);
      navigate(
        {
          pathname: '/',
          search: patchAdminSearch(
            location.search,
            { page: 'proposals', action: 'add' },
            ['from', 'proposal', 'edit', 'ptab'],
          ),
        },
        { replace: true },
      );
      return;
    }
    setActiveTab(proposalTabFromSearch(location.search));
    setViewId(next.get('proposal') || null);
    setEditId(next.get('edit') || null);
    setShowCreate(next.get('action') === 'add');
  }, [location.search, navigate]);

  const handleDelete = async (row) => {
    const label = row.proposalNo || row.title || 'this proposal';
    if (!await appConfirmMessage(`Delete ${label}? This cannot be undone.`)) return;
    try {
      setDeletingId(row.id);
      await graphqlRequest(REMOVE_PROPOSAL, { id: row.id });
      if (viewId === row.id) closeProposal();
      if (editId === row.id) closeEdit();
      await fetchProposals();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to delete proposal');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchTab = activeTab === 'ALL' || row.status === activeTab;
      const matchSearch =
        !term ||
        row.proposalNo.toLowerCase().includes(term) ||
        row.title.toLowerCase().includes(term) ||
        row.client.toLowerCase().includes(term);
      return matchTab && matchSearch;
    });
  }, [rows, search, activeTab]);

  const counts = useMemo(() => {
    const base = { ALL: rows.length, DRAFT: 0, SENT: 0, VIEWED: 0, REVISION: 0, SIGNED: 0, DECLINED: 0, EXPIRED: 0 };
    rows.forEach((r) => {
      if (base[r.status] != null) base[r.status] += 1;
    });
    return base;
  }, [rows]);

  const expiringSoonCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.status !== 'EXPIRED' &&
          r.status !== 'SIGNED' &&
          r.expiresAt &&
          isExpiringSoon(r.expiresAt),
      ).length,
    [rows],
  );

  const followUpCount = useMemo(
    () => rows.filter((r) => needsFollowUp(r.raw)).length,
    [rows],
  );

  return (
    <div className="prop-page">
      <h1 className="prop-title">Proposal Dashboard</h1>

      <div className="prop-toolbar">
        <div className="prop-search">
          <FiSearch className="prop-search-icon" size={16} />
          <input
            type="search"
            placeholder="Search by proposal number, client, or title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="prop-create-btn"
          onClick={() => openCreate()}
        >
          <FiPlus size={16} />
          Create Proposal
        </button>
      </div>

      <div className="prop-kpi-grid">
        <div className="prop-kpi-card">
          <div className="prop-kpi-icon prop-kpi-icon--total"><FiLayers /></div>
          <div>
            <p className="prop-kpi-label">Total Proposals</p>
            <p className="prop-kpi-value">{counts.ALL}</p>
          </div>
        </div>
        <div className="prop-kpi-card">
          <div className="prop-kpi-icon prop-kpi-icon--draft"><FiEdit3 /></div>
          <div>
            <p className="prop-kpi-label">Draft</p>
            <p className="prop-kpi-value">{counts.DRAFT}</p>
          </div>
        </div>
        <div className="prop-kpi-card">
          <div className="prop-kpi-icon prop-kpi-icon--sent"><FiSend /></div>
          <div>
            <p className="prop-kpi-label">Sent / Awaiting</p>
            <p className="prop-kpi-value">{counts.SENT + counts.VIEWED}</p>
          </div>
        </div>
        <div className="prop-kpi-card">
          <div className="prop-kpi-icon prop-kpi-icon--signed"><FiCheckCircle /></div>
          <div>
            <p className="prop-kpi-label">Accepted / Signed</p>
            <p className="prop-kpi-value">{counts.SIGNED}</p>
          </div>
        </div>
        <div className="prop-kpi-card">
          <div className="prop-kpi-icon prop-kpi-icon--expiring"><FiClock /></div>
          <div>
            <p className="prop-kpi-label">Expiring Soon</p>
            <p className="prop-kpi-value">{expiringSoonCount}</p>
          </div>
        </div>
      </div>

      {followUpCount > 0 && !alertDismissed && (
        <div className="prop-alert">
          <div className="prop-alert-left">
            <FiAlertTriangle size={16} />
            <span>
              {followUpCount} proposal{followUpCount > 1 ? 's have' : ' has'} been viewed by
              the client but not responded to in 5+ days — follow up now
            </span>
          </div>
          <button
            type="button"
            className="prop-alert-close"
            onClick={() => setAlertDismissed(true)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <div className="prop-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`prop-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => selectStatusTab(tab.key)}
          >
            {tab.label} ({counts[tab.key] ?? 0})
          </button>
        ))}
      </div>

      <div className="prop-panel">
        <div className="prop-table-wrap">
          <table className="prop-table">
            <thead>
              <tr>
                <th>Proposal No.</th>
                <th>Title</th>
                <th>Client</th>
                <th>Value</th>
                <th>Sent Date</th>
                <th>Expiry</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="prop-loading">Loading proposals…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="prop-empty">No proposals found.</td>
                </tr>
              ) : (
                filtered.map((row) => {
                  const statusMeta = getStatusMeta(row.status);
                  const expirySoon =
                    row.expiresAt &&
                    isExpiringSoon(row.expiresAt) &&
                    row.status !== 'EXPIRED';
                  const expired = isExpired(row.expiresAt, row.status);

                  return (
                    <tr key={row.id}>
                      <td>
                        <button type="button" className="prop-link" onClick={() => openProposal(row.id)}>
                          {row.proposalNo}
                        </button>
                      </td>
                      <td className="prop-title-cell" title={row.title}>{row.title}</td>
                      <td>
                        <button type="button" className="prop-link" onClick={() => openProposal(row.id)}>
                          {row.client}
                        </button>
                      </td>
                      <td>{formatAUD(row.value)}</td>
                      <td style={row.status === 'DRAFT' ? { color: '#94a3b8' } : undefined}>
                        {row.sentDateLabel || '—'}
                      </td>
                      <td className={expired ? 'prop-expiry--expired' : expirySoon ? 'prop-expiry--soon' : ''}>
                        {expirySoon && <FiAlertTriangle size={13} className="prop-expiry-warn" />}
                        {row.expiryLabel || '—'}
                      </td>
                      <td>
                        <span className={`prop-status ${statusMeta.className}`}>{statusMeta.label}</span>
                      </td>
                      <td>
                        <div className="prop-actions">
                          <button type="button" className="prop-action-btn" title="View" onClick={() => openProposal(row.id)}>
                            <FiEye size={14} />
                          </button>
                          <button type="button" className="prop-action-btn" title="Edit" onClick={() => openEdit(row.id)}>
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            type="button"
                            className="prop-action-btn prop-action-btn--danger"
                            title="Delete"
                            disabled={deletingId === row.id}
                            onClick={() => handleDelete(row)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateProposalModal
          prefill={createPrefill || undefined}
          onClose={closeCreate}
          onCreated={() => fetchProposals()}
        />
      )}

      {editId && (
        <CreateProposalModal
          proposalId={editId}
          onClose={closeEdit}
          onSaved={() => {
            closeEdit();
            fetchProposals();
          }}
        />
      )}

      {viewId && (
        <ProposalViewModal proposalId={viewId} onClose={closeProposal} />
      )}
    </div>
  );
}
